import { router } from "@inertiajs/react";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface AddDomainModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DomainExample {
  example: string;
  description: string;
}

const DOMAIN_EXAMPLES: DomainExample[] = [
  {
    example: "example.com",
    description: "Your main domain",
  },
  {
    example: "blog.example.com",
    description: "A specific subdomain",
  },
  {
    example: "*.example.com",
    description: "Wildcard for all subdomains and main domain",
  },
];

export function AddDomainModal({ open, onOpenChange }: AddDomainModalProps) {
  const [domain, setDomain] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!domain.trim()) {
      setErrorMessage("Domain is required");
      setSubmitStatus("error");
      return;
    }

    const trimmedDomain = domain.trim();

    router.post(
      "/domains",
      {
        hostname: trimmedDomain,
      },
      {
        forceFormData: true,
        preserveState: true,
        onStart: () => {
          setIsSubmitting(true);
          setSubmitStatus("idle");
          setErrorMessage("");
        },
        onSuccess: (page) => {
          const flash = (page.props as { flash?: { notice?: string; alert?: string } }).flash;

          if (flash?.notice) {
            setDomain("");
            setErrorMessage("");
            setSubmitStatus("idle");
            onOpenChange(false);
            return;
          }

          if (flash?.alert) {
            setErrorMessage(flash.alert);
            setSubmitStatus("error");
          }
        },
        onError: (errors) => {
          const errorMsg = errors.hostname || "Failed to register domain";
          setErrorMessage(errorMsg);
          setSubmitStatus("error");
        },
        onFinish: () => {
          setIsSubmitting(false);
        },
      },
    );
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setDomain("");
      setSubmitStatus("idle");
      setErrorMessage("");
      onOpenChange(false);
    }
  };

  const getStatusIcon = () => {
    if (submitStatus === "success") {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    } else if (submitStatus === "error") {
      return <AlertCircle className="h-5 w-5 text-red-600" />;
    }
    return null;
  };

  const getStatusMessage = () => {
    if (submitStatus === "success") {
      return "Domain registered successfully! Reloading...";
    } else if (submitStatus === "error") {
      return errorMessage;
    }
    return "";
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Domain</DialogTitle>
          <DialogDescription>
            Register a domain to automatically generate OG images for your URLs. You can also use wildcard domains to
            cover all subdomains.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="domain">Domain Name</Label>
            <Input
              id="domain"
              type="text"
              placeholder="example.com or *.example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              disabled={isSubmitting}
              className={submitStatus === "error" ? "border-red-500" : ""}
            />

            <div className="lh-1 flex items-start gap-2 text-sm">
              {getStatusIcon()}
              {getStatusMessage()}
            </div>

            <p className="text-muted-foreground text-xs">Examples:</p>
            <div className="space-y-2">
              {DOMAIN_EXAMPLES.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-xs">
                  <code className="bg-muted rounded px-2 py-1 font-mono">{item.example}</code>
                  <span className="text-muted-foreground">{item.description}</span>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !domain.trim()}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registering...
                </>
              ) : (
                "Register Domain"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
